/** Client tipizzato del worker di routing (singleton, richieste con id). */

import type { PlanResult } from '../lib/route';
import type { HeatmapEdge, RouteShapeSegment, SweepPoint } from '../lib/sweep';
import type {
  ComputeParams,
  DataMeta,
  ProgressPhase,
  WorkerRequest,
  WorkerResponse,
} from '../workers/protocol';

export interface PlanOutcome {
  plan: PlanResult;
  meta: DataMeta;
}

export class SupersededError extends Error {
  constructor() {
    super('richiesta sostituita da una più recente');
    this.name = 'SupersededError';
  }
}

type Pending = {
  kind: WorkerRequest['type'];
  onProgress?: (phase: ProgressPhase) => void;
  resolve: (r: WorkerResponse) => void;
  reject: (e: Error) => void;
};

let worker: Worker | null = null;
let seq = 0;
const pending = new Map<number, Pending>();

function getWorker(): Worker {
  if (!worker) {
    worker = new Worker(new URL('../workers/routing.worker.ts', import.meta.url), {
      type: 'module',
    });
    worker.onmessage = (e: MessageEvent<WorkerResponse>) => {
      const msg = e.data;
      const p = pending.get(msg.id);
      if (!p) return; // risposta di una richiesta ormai superata
      if (msg.type === 'progress') {
        p.onProgress?.(msg.phase);
        return;
      }
      pending.delete(msg.id);
      if (msg.type === 'error') p.reject(new Error(msg.message));
      else p.resolve(msg);
    };
    worker.onerror = (e) => {
      for (const [id, p] of pending) {
        pending.delete(id);
        p.reject(new Error(`Errore nel worker di routing: ${e.message}`));
      }
    };
  }
  return worker;
}

function send(
  req: WorkerRequest,
  onProgress?: (phase: ProgressPhase) => void,
): Promise<WorkerResponse> {
  const w = getWorker();
  return new Promise<WorkerResponse>((resolve, reject) => {
    pending.set(req.params.id, { kind: req.type, onProgress, resolve, reject });
    w.postMessage(req);
  });
}

/** Calcolo del percorso: una nuova richiesta rende obsolete le precedenti dello stesso tipo. */
export function requestPlan(
  params: Omit<ComputeParams, 'id'>,
  onProgress: (phase: ProgressPhase) => void,
): { id: number; promise: Promise<PlanOutcome> } {
  const id = ++seq;
  for (const [oldId, p] of pending) {
    if (p.kind === 'compute') {
      pending.delete(oldId);
      p.reject(new SupersededError());
    }
  }
  const promise = send({ type: 'compute', params: { ...params, id } }, onProgress).then((msg) => {
    if (msg.type !== 'result') throw new Error('risposta inattesa dal worker');
    return { plan: msg.plan, meta: msg.meta };
  });
  return { id, promise };
}

/** Ombra dello stesso percorso a orari di partenza diversi. */
export function requestSweep(params: {
  segments: RouteShapeSegment[];
  fromMs: number;
  stepMs: number;
  count: number;
}): Promise<SweepPoint[]> {
  const id = ++seq;
  return send({ type: 'sweep', params: { ...params, id } }).then((msg) => {
    if (msg.type !== 'sweep') throw new Error('risposta inattesa dal worker');
    return msg.points;
  });
}

/** Ombra di tutte le vie dell'area a un orario. */
export function requestHeatmap(tMs: number): Promise<HeatmapEdge[]> {
  const id = ++seq;
  return send({ type: 'heatmap', params: { id, tMs } }).then((msg) => {
    if (msg.type !== 'heatmap') throw new Error('risposta inattesa dal worker');
    return msg.edges;
  });
}
